import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  User,
  Calendar,
  Eye,
  BarChart,
  Loader2,
  Download,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { analyticsAPI } from "@/services/api";
import { format } from "date-fns";
import config from "../../config/config";
import ExamDetailView from "@/components/admin/ExamDetailView";

const Analytics = () => {
  const [selectedCandidateId, setSelectedCandidateId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showExamDetail, setShowExamDetail] = useState(false);
  const [examResultsPage, setExamResultsPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();

  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const EXAMS_PER_PAGE = 3; // Reduced for modal
  const CANDIDATES_PER_PAGE = 5; // For table pagination

  // Query for candidates list - API returns candidates sorted by creation date (newest first)
  const {
    data: candidatesData,
    isLoading: candidatesLoading,
    isError: candidatesError,
  } = useQuery({
    queryKey: ["analytics-candidates", currentPage, searchTerm, filterType],
    queryFn: () =>
      analyticsAPI.getCandidates({
        page: currentPage,
        limit: CANDIDATES_PER_PAGE,
        search: searchTerm,
        filter: filterType,
      }),
    enabled: true,
  });

  // Query for candidate details
  const {
    data: candidateDetails,
    isLoading: detailsLoading,
    isError: detailsError,
  } = useQuery({
    queryKey: ["candidate-details", selectedCandidateId],
    queryFn: () => analyticsAPI.getCandidateDetails(selectedCandidateId),
    enabled: !!selectedCandidateId && showModal,
  });

  // Query for exam detail data
  const {
    data: examDetailData,
    isLoading: examDetailLoading,
  } = useQuery({
    queryKey: ["exam-detail", selectedSessionId],
    queryFn: () => analyticsAPI.getExamSessionDetail(selectedSessionId),
    enabled: !!selectedSessionId && showExamDetail,
  });

  // Reset states when candidate changes
  useEffect(() => {
    setSelectedSessionId("");
    setShowExamDetail(false);
    setExamResultsPage(1);
  }, [selectedCandidateId]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
    // Clear sorting when search/filter changes to show newest first
    setSortField('');
    setSortOrder('asc');
  }, [searchTerm, filterType]);

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  const sortCandidates = (candidates, sortField, sortOrder) => {
    // If no sorting is applied, return candidates in their original order (newest first from API)
    // This ensures newly created candidates appear at the top across all pages
    if (!sortField) return candidates;

    return [...candidates].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortField.includes('.')) {
        const fields = sortField.split('.');
        aValue = fields.reduce((obj, field) => obj?.[field], a);
        bValue = fields.reduce((obj, field) => obj?.[field], b);
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      // Handle date fields
      if (sortField === 'createdAt') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }

      // Handle string fields
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const handleCandidateView = (candidateId) => {
    setSelectedCandidateId(candidateId);
    setShowModal(true);
    setShowExamDetail(false);
  };

  const handleExamSelect = (examId, resultId) => {
    setSelectedSessionId(resultId);
    setShowExamDetail(true);
  };

  const handleBackToOverview = () => {
    setShowExamDetail(false);
    setSelectedSessionId("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCandidateId("");
    setShowExamDetail(false);
    setExamResultsPage(1);
  };

  // Direct PDF download without redirects
  const downloadPDF = async (url, filename) => {
    try {
      setIsDownloading(true);
      const token = localStorage.getItem("token");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download report");
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Success",
        description: "Report downloaded successfully",
      });
    } catch (error) {
      console.error("Error downloading report:", error);
      toast({
        title: "Error",
        description: "Failed to download report",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportReport = async () => {
    if (!selectedCandidateId) return;

    const candidate = candidateDetails?.data?.candidate;
    const filename = `candidate-report-${candidate?.name || "unknown"}.pdf`;
    await downloadPDF(
      `${config.API_URL}/analytics/export/${selectedCandidateId}`,
      filename
    );
  };

  const handleExamDetailExport = async () => {
    if (!selectedSessionId) return;

    const candidate = examDetailData?.data?.candidate;
    const exam = examDetailData?.data?.exam;
    const filename = `exam-detail-${candidate?.name || "unknown"}-${
      exam?.name || "exam"
    }.pdf`;
    await downloadPDF(
      `${config.API_URL}/analytics/export-exam/${selectedSessionId}`,
      filename
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: "bg-green-100 text-green-800 border-green-200",
      completed: "bg-blue-100 text-blue-800 border-blue-200",
      ongoing: "bg-yellow-100 text-yellow-800 border-yellow-200",
      disqualified: "bg-red-100 text-red-800 border-red-200",
      inactive: "bg-gray-100 text-gray-800 border-gray-200",
    };

    return (
      <Badge className={statusConfig[status] || statusConfig.inactive}>
        {status || "inactive"}
      </Badge>
    );
  };

  const renderCandidatesTable = () => {
    const candidates = candidatesData?.data?.candidates || [];
    const pagination = candidatesData?.data?.pagination || {};

    // Sort the candidates
    const sortedCandidates = sortCandidates(candidates, sortField, sortOrder);

    return (
      <div className="space-y-4">
        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search candidates by name or email..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10"
            />
          </div>
          <div className="w-full md:w-48">
            <Select value={filterType} onValueChange={handleFilterChange}>
              <SelectTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter candidates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Candidates</SelectItem>
                <SelectItem value="my">My Candidates</SelectItem>
                <SelectItem value="company">Company Candidates</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>S.No</TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center">
                    Name
                    {getSortIcon('name')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('email')}
                >
                  <div className="flex items-center">
                    Email
                    {getSortIcon('email')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('createdBy.name')}
                >
                  <div className="flex items-center">
                    Created By
                    {getSortIcon('createdBy.name')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    Status
                    {getSortIcon('status')}
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center">
                    Created Date
                    {getSortIcon('createdAt')}
                  </div>
                </TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidatesLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
                    <p className="mt-2 text-sm text-gray-500">
                      Loading candidates...
                    </p>
                  </TableCell>
                </TableRow>
              ) : candidatesError ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-red-500"
                  >
                    Error loading candidates. Please try again.
                  </TableCell>
                </TableRow>
              ) : sortedCandidates.length > 0 ? (
                sortedCandidates.map((candidate, index) => (
                  <TableRow key={candidate._id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">
                      {(currentPage - 1) * CANDIDATES_PER_PAGE + (index + 1)}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-2 text-gray-400" />
                        {candidate.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {candidate.email}
                    </TableCell>
                    <TableCell>
                      {candidate.createdBy ? (
                        <Badge variant="outline" className="text-xs">
                          {candidate.createdBy.name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(candidate.status)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">
                        {format(new Date(candidate.createdAt), "MMM dd, yyyy")}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCandidateView(candidate._id)}
                        className="h-8 w-8 p-0"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-gray-500"
                  >
                    No candidates found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Page {pagination.current} of {pagination.pages} (
              {pagination.total} total)
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.current - 1)}
                disabled={pagination.current <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.current + 1)}
                disabled={pagination.current >= pagination.pages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCandidateOverview = () => {
    if (detailsLoading) {
      return (
        <div className="flex justify-center items-center h-40">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      );
    }

    if (detailsError || !candidateDetails?.data) {
      return (
        <div className="text-center p-6 text-red-500 text-sm">
          Failed to load candidate details. Please try again.
        </div>
      );
    }

    const { candidate, stats, examResults } = candidateDetails.data.data;

    // Pagination for exam results
    const totalExamPages = Math.ceil(examResults.length / EXAMS_PER_PAGE);
    const startIndex = (examResultsPage - 1) * EXAMS_PER_PAGE;
    const paginatedResults = examResults.slice(
      startIndex,
      startIndex + EXAMS_PER_PAGE
    );

    return (
      <div className="space-y-4">
        {/* Compact Candidate Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-4">
            <CardHeader className="pb-2 p-0">
              <CardTitle className="text-base">Candidate Information</CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-2">
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Name:</span>
                  <span className="font-medium">{candidate.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email:</span>
                  <span className="truncate ml-2">{candidate.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Mobile:</span>
                  <span>{candidate.mobileNumber || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status:</span>
                  {getStatusBadge(candidate.status)}
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Registered:</span>
                  <span className="text-xs">
                    {format(new Date(candidate.createdAt), "MMM dd, yyyy")}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="p-4">
            <CardHeader className="pb-2 p-0">
              <CardTitle className="text-base">
                Performance Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-primary">
                    {stats.totalExams}
                  </div>
                  <div className="text-gray-500">Exams</div>
                </div>
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-primary">
                    {stats.avgScore}%
                  </div>
                  <div className="text-gray-500">Avg Score</div>
                </div>
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-green-600">
                    {stats.bestScore}%
                  </div>
                  <div className="text-gray-500">Best</div>
                </div>
                <div className="text-center p-2 border rounded text-xs">
                  <div className="text-xl font-bold text-orange-500">
                    {stats.worstScore}%
                  </div>
                  <div className="text-gray-500">Lowest</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Compact Exam History */}
        <Card className="p-4">
          <CardHeader className="pb-2 p-0 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Exam History</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportReport}
              disabled={isDownloading}
              className="h-7 px-2 text-xs"
            >
              {isDownloading ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Download className="w-3 h-3 mr-1" />
              )}
              Export
            </Button>
          </CardHeader>
          <CardContent className="p-0 pt-2">
            {examResults && examResults.length > 0 ? (
              <div className="space-y-3">
                <div className="border rounded text-sm">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="h-8">Exam</TableHead>
                        <TableHead className="h-8">Date</TableHead>
                        <TableHead className="h-8">Score</TableHead>
                        <TableHead className="h-8">Q&A</TableHead>
                        <TableHead className="h-8 text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedResults.map((result) => (
                        <TableRow key={result.resultId} className="text-xs">
                          <TableCell className="font-medium py-2">
                            <div
                              className="truncate max-w-32"
                              title={result.examName}
                            >
                              {result.examName}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-gray-400" />
                              {format(new Date(result.dateCompleted), "MMM dd")}
                            </div>
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge
                              className={
                                result.score >= 70
                                  ? "bg-green-100 text-green-800 border-green-200 text-xs px-1"
                                  : result.score >= 40
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-200 text-xs px-1"
                                  : "bg-red-100 text-red-800 border-red-200 text-xs px-1"
                              }
                            >
                              {result.score}%
                            </Badge>
                          </TableCell>
                          <TableCell className="py-2">
                            {result.correctAnswers}/{result.totalQuestions}
                          </TableCell>
                          <TableCell className="text-right py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleExamSelect(result.examId, result.resultId)
                              }
                              className="h-6 w-6 p-0"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Compact Pagination for exam results */}
                {totalExamPages > 1 && (
                  <div className="flex items-center justify-between text-xs">
                    <div className="text-gray-500">
                      {startIndex + 1}-
                      {Math.min(
                        startIndex + EXAMS_PER_PAGE,
                        examResults.length
                      )}{" "}
                      of {examResults.length}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setExamResultsPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={examResultsPage <= 1}
                        className="h-6 px-2"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                      <span className="text-gray-500">
                        {examResultsPage}/{totalExamPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setExamResultsPage((prev) =>
                            Math.min(totalExamPages, prev + 1)
                          )
                        }
                        disabled={examResultsPage >= totalExamPages}
                        className="h-6 px-2"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4 text-gray-500 text-sm">
                No exam history available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Analytics</h1>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center">
            <BarChart className="h-5 w-5 mr-2" /> Candidate Performance
            Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>{renderCandidatesTable()}</CardContent>
      </Card>

      {/* Modal for Candidate Overview */}
      <Dialog open={showModal} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-7xl min-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                {showExamDetail && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToOverview}
                    className="h-6 px-2"
                  >
                    <ArrowLeft className="h-3 w-3" />
                  </Button>
                )}
                Candidate Analytics
              </span>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            {showExamDetail ? (
              <ExamDetailView
                sessionData={examDetailData?.data}
                onExportPDF={handleExamDetailExport}
                isLoading={examDetailLoading}
                isDownloading={isDownloading}
              />
            ) : (
              renderCandidateOverview()
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
